import { GOOGLE_ADDRESS_API_KEY } from "@/lib/constants";
import { Input } from "@chakra-ui/react";
import { CSSObject } from "@emotion/react";
import GooglePlacesAutocomplete, {
  geocodeByPlaceId,
} from "react-google-places-autocomplete";

type PrefillField = "City" | "State" | "Zip" | "city" | "state" | "zip";
interface AddressComponent {
  long_name: string;
  short_name: string;
  types: string[];
}

const extractAddress = (data: { address_components: AddressComponent[] }) => {
  const addressComponents = data.address_components;

  // Initialize the result object
  const result = {
    StreetAddress: "",
    City: "",
    State: "",
    Zip: "",
  };

  // Extract street address
  const streetNumber =
    addressComponents.find(({ types }) => types.includes("street_number"))
      ?.long_name || "";
  const route =
    addressComponents.find(({ types }) => types.includes("route"))?.long_name ||
    "";
  result.StreetAddress = `${streetNumber} ${route}`.trim();

  result.City =
    addressComponents.find(
      ({ types }) =>
        types.includes("political") &&
        (types.includes("locality") || types.includes("sublocality"))
    )?.long_name || "";

  result.State =
    addressComponents.find(({ types }) =>
      types.includes("administrative_area_level_1")
    )?.long_name || "";

  // Extract zip code
  result.Zip =
    addressComponents.find(({ types }) => types.includes("postal_code"))
      ?.long_name || "";

  return result;
};

const GoogleAddressInput = ({
  value,
  onChange,
  customCityKey,
  customZipKey,
  customStateKey,
  prefillFields = [],
  setValue,
  disabled = false,
}: {
  value: string;
  onChange: (_val: string) => void;
  customCityKey?: string;
  customZipKey?: string;
  customStateKey?: string;
  prefillFields?: PrefillField[];
  setValue?: Function;
  disabled?: boolean;
}) => {
  const getAddressComponents = async (place_id: string) => {
    const response = await geocodeByPlaceId(place_id);
    const fields: {
      [PrefillField: string]: string;
    } = extractAddress(response[0]);

    prefillFields.forEach((field: PrefillField) => {
      if (setValue) {
        setValue(field, fields[field]);
      }
    });
    if (setValue) {
      if (customCityKey) {
        setValue(customCityKey, fields.City);
      }
      if (customZipKey) {
        setValue(customZipKey, fields.Zip);
      }
      if (customStateKey) {
        setValue(customStateKey, fields.State);
      }
    }
  };

  return (
    <Input
      as={GooglePlacesAutocomplete}
      isDisabled
      apiKey={GOOGLE_ADDRESS_API_KEY}
      autocompletionRequest={{
        componentRestrictions: {
          country: ["us"],
        },
      }}
      w="auto"
      selectProps={{
        isDisabled: disabled,
        placeholder: "Search for address...",
        value: value
          ? {
              label: value,
              value: "",
            }
          : undefined,
        onChange: (val: any) => {
          if (val && val.label) {
            if (
              (prefillFields.length > 0 ||
                customCityKey ||
                customStateKey ||
                customZipKey) &&
              val.value.structured_formatting.main_text
            ) {
              onChange(val.value.structured_formatting.main_text);
            } else onChange(val.label);
          }
          if (
            (prefillFields.length > 0 ||
              customCityKey ||
              customStateKey ||
              customZipKey) &&
            val &&
            val.value &&
            val.value.place_id
          ) {
            getAddressComponents(val.value.place_id);
          }
        },
        components: {
          DropdownIndicator: () => null,
          IndicatorSeparator: () => null,
        },
        styles: {
          container: (provided: CSSObject) => ({
            ...provided,
            width: "100%",
          }),
          control: (provided: CSSObject) => ({
            ...provided,
            width: "100%",
            height: "40px",
            borderColor: "#E2E8F0",
            borderRadius: "0.375rem",
            paddingLeft: ".25rem",
            paddingRight: ".25rem",
          }),
        },
      }}
    />
  );
};

export default GoogleAddressInput;
